import { get, isNil, gte, debounce, isEqual } from 'lodash';
import { BehaviorSubject } from 'rxjs';
import { Component, ViewChild, ElementRef } from '@angular/core';
import { IHeaderAngularComp } from 'ag-grid-angular';
import {
  GridApi,
  IHeaderParams,
  RowNode,
  IDatasource,
} from 'ag-grid-community';
import { getDisplayedSelectedRows } from '../custom-components/cell-renderer-components/renderer-helpers';

// eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection
@Component({
  selector: 'xvi-custom-select-all',
  templateUrl: './custom-select-all.component.html',
  styleUrls: ['./custom-select-all.component.scss'],
})
export class CustomSelectAllComponent implements IHeaderAngularComp {
  @ViewChild('headerSelectAllCheckbox', { static: true })
  public selectAllCheckbox: ElementRef;

  public isIndeterminate$: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);
  public isEveryRowSelected$: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);

  /**
   * max select limit, null / undefined means no limit
   */
  public maxSelectLimit: number;

  private gridApi: GridApi;
  private params: IHeaderParams;
  private dataSource: IDatasource;

  public agInit(params: IHeaderParams): void {
    this.params = params;
    this.gridApi = this.params.api;
    this.dataSource = get(params, 'dataSource');
    this.maxSelectLimit = get(params, 'maxSelectLimit');

    this.gridApi.removeEventListener(
      'selectionChanged',
      this.calcCheckAllBtnUI.bind(this),
    );
    this.gridApi.removeEventListener(
      'filterChanged',
      this.calcCheckAllBtnUI.bind(this),
    );
    this.gridApi.addEventListener(
      'selectionChanged',
      this.calcCheckAllBtnUI.bind(this),
    );
    this.gridApi.addEventListener(
      'filterChanged',
      debounce(this.calcCheckAllBtnUI.bind(this), 100),
    );

    this.isIndeterminate$.next(this.isIndeterminate());
    this.isEveryRowSelected$.next(this.everyRowSelected());
  }

  public calcCheckAllBtnUI(): void {
    this.isIndeterminate$.next(this.isIndeterminate());
    this.isEveryRowSelected$.next(this.everyRowSelected());
  }

  public isMeetMaxSelectLimit(selectedCount: number): boolean {
    if (isNil(this.maxSelectLimit)) {
      return false;
    }

    return gte(selectedCount, this.maxSelectLimit);
  }

  public selectAllClick(): void {
    const value = this.selectAllCheckbox.nativeElement.checked;

    // when is deselect all, just deselect and return;
    if (!value) {
      this.gridApi.forEachNodeAfterFilterAndSort((row: RowNode): void => {
        row.setSelected(false);
      });

      return;
    }

    let selectedCount = this.gridApi.getSelectedRows().length;
    let isMeetMaxSelectLimit = false;

    this.gridApi.forEachNodeAfterFilterAndSort((row: RowNode): void => {
      // just return when is meet max select limit or row is already selected or row is not selectable
      if (isMeetMaxSelectLimit || row.isSelected() || !row.selectable) {
        return;
      }

      row.setSelected(true);
      selectedCount = selectedCount + 1;

      isMeetMaxSelectLimit = this.isMeetMaxSelectLimit(selectedCount);
    });
  }

  public isIndeterminate(): boolean {
    const selectedRowsCount = getDisplayedSelectedRows(this.gridApi).length;
    if (!selectedRowsCount) return false;

    return (
      !isEqual(selectedRowsCount, this.getTotalRowsCount()) &&
      !this.isMeetMaxSelectLimit(selectedRowsCount)
    );
  }

  public everyRowSelected(): boolean {
    const selectedRowsCount = getDisplayedSelectedRows(this.gridApi).length;
    const totalRowsCount = this.getTotalRowsCount();

    return (
      totalRowsCount > 0 &&
      (isEqual(selectedRowsCount, totalRowsCount) ||
        this.isMeetMaxSelectLimit(selectedRowsCount))
    );
  }

  private getTotalRowsCount(): number {
    let rows = this.gridApi.getDisplayedRowCount();
    const total = get(this.dataSource, 'rowCount');
    const isLastRowFound = this.gridApi.getModel().isLastRowFound();
    if (total) rows = total;

    // in falsy case we are decrementing rows count by 1 because ag-grid adds additional row
    // to reserve place for loader purpose
    return isLastRowFound ? rows : rows - 1;
  }
}
